import { getFirstPartyVisitorId } from '@/lib/analytics/first-party-visitor-id';
import { getEnv } from '@/utils/env';

import { getConsentAuthToken } from './get-consent-auth-token';

export type ConsentDecisionEvent = 'shown' | 'accepted' | 'rejected';

export interface ConsentCategories {
  marketingConsent: boolean | null;
  preferencesConsent: boolean | null;
  statisticsConsent: boolean | null;
}

export interface RecordConsentEventInput extends ConsentCategories {
  /** Which CMP configuration produced this decision — see the inline comment below on why this is a cbid, not a version number. */
  cmpConfigVersion: string;
  /** The CMP's own id for this decision, so the backend can deduplicate repeat writes of the same choice. Null for `shown` and for a decision the CMP hasn't stamped yet. */
  cmpConsentStamp: string | null;
  event: ConsentDecisionEvent;
}

/**
 * Records one consent decision against our own evidence log — the source of truth for what a
 * visitor agreed to, independent of the CMP. Never throws: the banner's own UX must never break
 * because this write failed, so a network error or a non-2xx response is swallowed here rather
 * than surfaced to the caller. The backend's dedupe index (on consent_id + cmp_consent_stamp) means
 * a retry from a later pageview recovers a dropped write without our doing anything extra.
 *
 * `user_id` is deliberately NOT in that index: it is null on every pre-signup write, and MySQL
 * treats null as never equal to null in a unique index, so including it would stop anonymous
 * repeats from colliding at all — the dedupe would fail for exactly the traffic it exists for.
 */
export async function recordConsentEvent(input: RecordConsentEventInput): Promise<void> {
  // `API_URL` already carries the `/v1` prefix (e.g. https://dev-platform-api.extern.com/v1), so the
  // path appended here must not repeat it. The endpoint answers on the platform API host, but it is
  // not served by the platform API: apps/consent is its own stack with its own REST API, and the
  // CloudFront distribution in front of that host routes `/v1/consent-records*` to it. Both live
  // behind one hostname so this call needs no second base URL, not because they are one API.
  const baseUrl = getEnv('API_URL');

  if (!baseUrl) {
    return;
  }

  // Sent when the visitor happens to be signed in, so the backend can stamp the row with their
  // user_id. Absent for everyone else, which is the common case — the banner fires before a
  // session exists. The endpoint treats the header as optional and records either way.
  const token = await getConsentAuthToken();

  try {
    await fetch(`${baseUrl}/consent-records`, {
      method: 'POST',
      headers: token
        ? { 'Content-Type': 'application/json', Authorization: token }
        : { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        consent_id: getFirstPartyVisitorId(),
        event: input.event,
        statistics_consent: input.statisticsConsent,
        marketing_consent: input.marketingConsent,
        preferences_consent: input.preferencesConsent,
        cmp_config_version: input.cmpConfigVersion,
        cmp_consent_stamp: input.cmpConsentStamp,
        page_url: window.location.href,
      }),
    });
  } catch {
    // Network failure, CORS rejection, the endpoint being down — none of it should ever surface
    // to whatever called this. The visitor already saw their choice take effect in the banner.
  }
}
